// src/web/client/src/pages/Analytics.jsx
// Advanced Analytics Dashboard - Pro Plan Feature
// Uses separate components from components/analytics/

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { safeApi, isFeatureError } from '@/utils/api';
import { hasFeature } from '@/utils/planEntitlements';

// Analytics Components
import RevenueAttribution from '@/components/analytics/RevenueAttribution';

// The four sections. Which one shows is decided by the Results page's tab
// strip (pages/Results.jsx) and passed in as `tab`; this screen renders no
// navigation of its own (one tab component everywhere — UX brief, Phase B).
export const VALID_TABS = ['overview', 'messages', 'attribution', 'segments'];

// ============================================
// MAIN ANALYTICS PAGE COMPONENT
// ============================================

// `embedded`: hosted inside Results (no header of its own; the period selector
// stays). `tab`: the section to render; anything unknown → Overview.
export default function Analytics({ embedded = false, tab = 'overview' } = {}) {
  const navigate = useNavigate();
  const activeTab = VALID_TABS.includes(tab) ? tab : 'overview';

  // State
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [analyticsData, setAnalyticsData] = useState(null);
  const [segmentsData, setSegmentsData] = useState(null);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [segmentsError, setSegmentsError] = useState(false);
  const [messagesData, setMessagesData] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(false);
  // Attribution data for the Overview Ecomoria-story cards + chart.
  // Both endpoints already exist and feed the dashboard hero; same
  // requireFeature('analytics') gate as /analytics itself.
  const [attribution, setAttribution] = useState(null); // GET /attribution → summary
  const [timeline, setTimeline] = useState(null); // GET /attribution/timeline → series
  const [attributionLoading, setAttributionLoading] = useState(true);
  const [attributionError, setAttributionError] = useState(false);

  // Check subscription status.
  //
  // `canViewAnalytics` is the server's answer, not a plan-string guess. It was
  // `isPro || isBasic` with isPro spelled `plan === 'pro' || 'professional'`,
  // so the `scale` tier fell through to the trial branch and a $99.99 merchant
  // was shown "Analytics requires Basic or Pro plan" with nothing fetched.
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    canViewAnalytics: false,
    loaded: false
  });

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const data = await safeApi.get('/subscription/info');
        if (data && !data.error) {
          setSubscriptionStatus({
            canViewAnalytics: hasFeature(data, 'analytics'),
            loaded: true
          });
        }
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
      }
    };
    fetchSubscriptionStatus();
  }, []);

  // Fetch analytics data
  // Fetch analytics data - only if subscription allows
  // Fetch analytics data - only if subscription allows
  useEffect(() => {
    if (!subscriptionStatus.loaded) return; // Wait for subscription to load
    
    if (subscriptionStatus.canViewAnalytics) {
      fetchAnalyticsData();
      fetchAttributionData();
      // Messages loads lazily on tab entry, but once it's on screen (or the
      // user sits on the tab) a period change must refetch it too — otherwise
      // the cards silently keep showing the old period under a selector that
      // claims otherwise.
      if (messagesData || activeTab === 'messages') {
        fetchMessagesData();
      }
    } else {
      // User is on Trial - show upgrade message
      setLoading(false);
      setError({
        error: true,
        code: 'FEATURE_NOT_AVAILABLE',
        feature: 'analytics',
        requiredPlan: 'Basic',
        message: 'Analytics requires Basic or Pro plan'
      });
    }
  }, [period, subscriptionStatus.loaded, subscriptionStatus.canViewAnalytics]);
  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const data = await safeApi.get(`/analytics?period=${period}`);
      
      if (isFeatureError(data)) {
        setError(data);
        return;
      }
      
      setAnalyticsData(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError({ message: 'Failed to load analytics data' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch the attribution summary + day-by-day timeline for the Overview.
  // Failure is a distinct state (error card + retry), never $0 cards — the
  // same three-state honesty rule the Segments tab follows since #134.
  const fetchAttributionData = async () => {
    setAttributionLoading(true);
    setAttributionError(false);
    try {
      const [attrRes, timelineRes] = await Promise.all([
        safeApi.get(`/attribution?period=${period}`),
        safeApi.get(`/attribution/timeline?period=${period}`)
      ]);
      const attr = attrRes?.data;
      const tl = timelineRes?.data;
      if (!isFeatureError(attrRes) && !isFeatureError(timelineRes)
          && attr?.summary && Array.isArray(tl?.series)) {
        setAttribution(attr);
        setTimeline(tl);
      } else {
        setAttributionError(true);
      }
    } catch (err) {
      console.error('Failed to fetch attribution data:', err);
      setAttributionError(true);
    } finally {
      setAttributionLoading(false);
    }
  };

  // Fetch segments data. A failed fetch must surface as an error state —
  // never as cards showing 0, which reads as "no customers" to the merchant.
  const fetchSegmentsData = async () => {
    setSegmentsLoading(true);
    setSegmentsError(false);
    try {
      const data = await safeApi.get('/analytics/segments');
      if (data?.success && Array.isArray(data.segments)) {
        setSegmentsData(data);
      } else {
        setSegmentsError(true);
      }
    } catch (err) {
      console.error('Failed to fetch segments data:', err);
      setSegmentsError(true);
    } finally {
      setSegmentsLoading(false);
    }
  };

  // Fetch messages performance data. Failure is a distinct state (error card
  // + retry) — the old silent catch left the tab on a spinner forever.
  const fetchMessagesData = async () => {
    setMessagesLoading(true);
    setMessagesError(false);
    try {
      const data = await safeApi.get(`/analytics/messages?period=${period}`);
      if (!isFeatureError(data) && data?.success) {
        setMessagesData(data);
      } else {
        setMessagesError(true);
      }
    } catch (err) {
      console.error('Failed to fetch messages data:', err);
      setMessagesError(true);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Load additional data when tab changes
  useEffect(() => {
    if (activeTab === 'segments' && !segmentsData) {
      fetchSegmentsData();
    } else if (activeTab === 'messages' && !messagesData) {
      fetchMessagesData();
    }
  }, [activeTab]);

  // Feature not available - show upgrade message
  if (error && error.code === 'FEATURE_NOT_AVAILABLE') {
    return <FeatureNotAvailable requiredPlan={error.requiredPlan} />;
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              {/* Hosted on Results the page already has a title and a subtitle;
                  only the period selector is this screen's own. Standalone,
                  the Ecomoria-story framing stays: what the app's emails do, in
                  honest "connected" (correlation) language. */}
              {!embedded && <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>}
              {!embedded && <p className="text-gray-500 mt-1">Your Ecomoria email activity and the revenue connected to it</p>}
            </div>
            
            {/* Period Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Period:</span>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="365d">Last year</option>
              </select>
            </div>
          </div>

          </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && activeTab === 'overview' ? (
          <LoadingState />
        ) : (
          <>
            {activeTab === 'overview' && (
              <OverviewTab
                data={analyticsData}
                error={error}
                onRetry={fetchAnalyticsData}
                attribution={attribution}
                timeline={timeline}
                attributionLoading={attributionLoading}
                attributionError={attributionError}
                onRetryAttribution={fetchAttributionData}
              />
            )}
            {activeTab === 'messages' && (
              <MessagesTab
                data={messagesData}
                loading={messagesLoading}
                error={messagesError}
                onRetry={fetchMessagesData}
              />
            )}
            {activeTab === 'attribution' && <RevenueAttribution compact={false} period={period} />}
            {activeTab === 'segments' && (
              <SegmentsTab
                data={segmentsData}
                loading={segmentsLoading}
                error={segmentsError}
                onRetry={fetchSegmentsData}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// TAB COMPONENTS
// ============================================

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      <span className="ml-3 text-gray-600">Loading analytics...</span>
    </div>
  );
}

function FeatureNotAvailable({ requiredPlan }) {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">📊</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Advanced Analytics</h2>
        <p className="text-gray-600 mb-6">
          Unlock powerful analytics to understand your customers better and grow your revenue.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm font-medium text-gray-700 mb-2">Includes:</p>
          <ul className="space-y-2">
            {[
              'Revenue attribution & ROI tracking',
              'Customer segments analysis',
              'Message performance insights',
              'VIP customer identification',
              'Personalized coupon generation'
            ].map((feature, i) => (
              <li key={i} className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>
        <button
          onClick={() => navigate('/billing')}
          className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          Upgrade to {requiredPlan === 'basic' ? 'Basic' : 'Pro'} Plan
        </button>
      </div>
    </div>
  );
}

// The Overview tells the Ecomoria story: what the app's emails did and the
// revenue connected to them. Store-wide Total Revenue / Total Orders cards,
// the store-wide charts and the Recent Orders list are gone — they duplicated
// Shopify's own admin, which is not this screen's job.
function OverviewTab({
  data,
  error,
  onRetry,
  attribution,
  timeline,
  attributionLoading,
  attributionError,
  onRetryAttribution,
}) {
  // A failed /analytics fetch must read as an error, never as an empty store —
  // "No analytics data available" for a fetch failure is the same dishonesty
  // the Segments tab shipped before #134.
  if (!data && error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center py-8">
        <p className="font-medium text-gray-900">Couldn't load analytics</p>
        <button
          onClick={onRetry}
          className="mt-3 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!data) return <EmptyState message="No analytics data available" />;

  const metrics = data.metrics || {};
  // Click rate = clicked/sent, computed from the raw counts the endpoint
  // already returns. NOT metrics.click_rate — that field is clicked/opened
  // (click-to-open), whose denominator is the MPP-inflated opens this card
  // stops headlining.
  const messagesSent = metrics.messages_sent || 0;
  const clickRate = messagesSent > 0
    ? (((metrics.messages_clicked || 0) / messagesSent) * 100).toFixed(1)
    : '0.0';

  const summary = attribution?.summary || {};
  // Window length comes from the response, never hardcoded — the backend owns
  // the attribution window (attribution_window_hours).
  const windowHours = timeline?.attribution_window_hours
    ?? attribution?.attribution_window_hours
    ?? 168;
  const windowDays = Math.round(windowHours / 24);
  const attributionReady = !attributionLoading && !attributionError;

  return (
    <div className="space-y-6">
      {/* One honest failure surface for the attribution block: the two
          attribution cards and the chart disappear together behind a single
          error card with retry — never $0s standing in for a failed fetch. */}
      {attributionError && (
        <div className="bg-white rounded-lg shadow p-6 text-center py-8">
          <p className="font-medium text-gray-900">Couldn't load attribution data</p>
          <button
            onClick={onRetryAttribution}
            className="mt-3 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200"
          >
            Try again
          </button>
        </div>
      )}

      {/* Key metrics — all four are Ecomoria's own story */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {attributionLoading && <MetricCardSkeleton />}
        {attributionReady && (
          <MetricCard
            title="Attributed Revenue"
            value={`$${Number(summary.total_attributed_revenue || 0).toLocaleString()}`}
            sublabel={`Purchases within ${windowDays} days of an Ecomoria email`}
          />
        )}
        <MetricCard
          title="Messages Sent"
          value={metrics.messages_sent || 0}
        />
        <MetricCard
          title="Click Rate"
          value={`${clickRate}%`}
        />
        {attributionLoading && <MetricCardSkeleton />}
        {attributionReady && (
          <MetricCard
            title="Attribution Rate"
            value={`${summary.attribution_rate || 0}%`}
            sublabel={`% of messages followed by a purchase within ${windowDays} days`}
          />
        )}
      </div>

      {/* Attributed revenue over time — from /attribution/timeline
          (buildAttributionTimeline): purchase-day bucketing, refund-aware net
          values, totals capped like the summary. Deliberately NOT
          daily_attribution — that series buckets by send day, sums the raw
          per-message snapshot (double-counts overlapping orders) and is
          uncapped, so its bars could sum to more than the Attributed Revenue
          card above. */}
      {!attributionError && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Attributed Revenue Over Time</h3>
          <p className="text-xs text-gray-400 mb-4">
            Purchases made within {windowDays} days of an Ecomoria email — correlation, not proof of cause.
          </p>
          {attributionLoading ? (
            <div data-testid="chart-skeleton" className="h-40 bg-gray-100 rounded animate-pulse" />
          ) : (
            <AttributedRevenueChart series={timeline?.series || []} windowDays={windowDays} />
          )}
        </div>
      )}
    </div>
  );
}

// Three distinct states (the #134 rule): skeleton while fetching, an honest
// error card with retry on failure, and the real cards otherwise. The old
// version initialized its own `loading` to true and never cleared it, so a
// failed fetch left the tab on a spinner forever.
function MessagesTab({ data, loading, error, onRetry }) {
  // No data and no error yet = the first fetch hasn't landed (the tab-entry
  // effect fires after first paint) — that's still "loading", never an empty
  // state.
  const pending = loading || (!data && !error);

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center py-8">
        <p className="font-medium text-gray-900">Couldn't load message performance</p>
        <button
          onClick={onRetry}
          className="mt-3 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200"
        >
          Try again
        </button>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              data-testid="messages-skeleton"
              className="border rounded-lg p-4 animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-3 bg-gray-200 rounded w-full mt-3" />
              <div className="h-3 bg-gray-200 rounded w-full mt-2" />
              <div className="h-3 bg-gray-200 rounded w-full mt-2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const messagePerformance = data?.message_performance || {};

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Performance by Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(messagePerformance).length > 0 ? (
            Object.entries(messagePerformance).map(([type, stats]) => (
              <div key={type} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 capitalize mb-2">
                  {type.replace(/_/g, ' ')}
                </h4>
                {/* Click / conversion / revenue — the trustworthy signals the
                    endpoint already returns (click_rate is clicked/sent).
                    Opened + Open Rate are gone, not demoted: opens are inflated
                    by Apple MPP / proxy prefetch, and a native title-tooltip
                    caveat is undiscoverable in the embedded iframe — same
                    stance as the attribution table (#86) and Messages (#109).
                    Labels mirror the attribution table's column headers. */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Sent</span>
                    <span className="font-medium">{stats.sent || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Click Rate</span>
                    <span className="font-medium">{stats.click_rate || 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Conv. Rate</span>
                    <span className="font-medium">{stats.conversion_rate || 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Revenue</span>
                    <span className="font-medium">${Number(stats.revenue || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center py-8 text-gray-500">
              No message data available for this period
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Cards follow the customer lifecycle ladder. `key` must equal the segment
// names computed by GET /api/analytics/segments (first_time/repeat/loyal/vip).
// Color classes are full literal strings — Tailwind cannot see interpolated
// class names at build time, so `bg-${color}-50` never reaches the prod CSS.
// `window` is each card's own time-basis pill: the ladder segments are
// lifetime, but Top Spenders follows the VIP tab's 180-day window (#156) —
// one header-level "All time" tag would lie for that card, so every card
// carries its own label instead.
const SEGMENT_CARDS = [
  { key: 'first_time', name: 'New Customers', description: 'First-time buyers', window: 'All time', colorClasses: 'border-green-500 bg-green-50' },
  { key: 'repeat', name: 'Repeat Customers', description: 'Coming back for more', window: 'All time', colorClasses: 'border-blue-500 bg-blue-50' },
  { key: 'loyal', name: 'Loyal Customers', description: 'Your frequent buyers', window: 'All time', colorClasses: 'border-amber-500 bg-amber-50' },
  // "Top Spenders", not "VIP" in the label — but since #156 this card uses
  // the SAME definition as the VIP tab (3 orders + $500 + $100 avg over
  // 180 days), so the two screens agree.
  { key: 'vip', name: 'Top Spenders', description: 'Your highest-spending customers', window: 'Last 180 days', colorClasses: 'border-purple-500 bg-purple-50' },
];

function SegmentsTab({ data, loading, error, onRetry }) {
  const segments = data?.segments || [];
  // No data and no error yet = the first fetch hasn't landed (the effect
  // fires after first paint) — that's still "loading", never zeros.
  const pending = loading || (!data && !error);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-gray-900">Customer Segments</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          New, Repeat and Loyal are based on each customer's full order history;
          Top Spenders looks at the last 180 days, matching the VIP tab.
          Not affected by the period selector.
        </p>
        {error ? (
          <div className="text-center py-8">
            <p className="font-medium text-gray-900">Couldn't load segments</p>
            <button
              onClick={onRetry}
              className="mt-3 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200"
            >
              Try again
            </button>
          </div>
        ) : pending ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SEGMENT_CARDS.map((segment) => (
              <div
                key={segment.key}
                data-testid="segment-skeleton"
                className="border-l-4 border-gray-200 bg-gray-50 p-4 rounded-r-lg animate-pulse"
              >
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-3 bg-gray-200 rounded w-1/2 mt-2" />
                <div className="h-7 bg-gray-200 rounded w-1/4 mt-3" />
              </div>
            ))}
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SEGMENT_CARDS.map((segment) => {
            const segmentData = segments.find(s => s.name === segment.key);
            const count = segmentData?.customer_count || 0;
            const avgSpent = Number(segmentData?.average_spent);
            return (
              <div
                key={segment.key}
                className={`border-l-4 p-4 rounded-r-lg ${segment.colorClasses}`}
              >
                <h4 className="font-medium text-gray-900 flex items-center justify-between gap-2">
                  {segment.name}
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap">
                    {segment.window}
                  </span>
                </h4>
                <p className="text-sm text-gray-500 mt-1">{segment.description}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{count}</p>
                {count > 0 && Number.isFinite(avgSpent) && (
                  <p className="text-sm text-gray-500 mt-1">
                    avg ${Math.round(avgSpent).toLocaleString()}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function MetricCard({ title, value, change, sublabel }) {
  const isPositive = change > 0;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        {change !== undefined && (
          <span className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <p className="mt-4 text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{title}</p>
      {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
    </div>
  );
}

function MetricCardSkeleton() {
  return (
    <div data-testid="metric-skeleton" className="bg-white rounded-lg shadow p-6 animate-pulse">
      <div className="h-6 w-6 bg-gray-200 rounded" />
      <div className="h-7 bg-gray-200 rounded w-1/2 mt-4" />
      <div className="h-4 bg-gray-200 rounded w-2/3 mt-2" />
    </div>
  );
}

// Local chart for the Overview — deliberately NOT imported from
// RevenueAttribution.jsx (that tab stays untouched); same visual language as
// its AttributionChart (green = attributed revenue) so both tabs read alike.
// series: [{ date, emails_sent, repeat_orders, repeat_revenue }] from
// GET /attribution/timeline — every day in range present, gaps are real zeros.
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function AttributedRevenueChart({ series, windowDays }) {
  if (!series || series.length === 0) {
    return (
      <div className="h-40 flex items-center justify-center text-gray-400">
        No data for this period
      </div>
    );
  }

  // Bucket long ranges by SUMMING revenue per bucket — a revenue chart
  // aggregated by max would show each bucket's peak day, not its total.
  const MAX_BARS = 30;
  const bucketSize = Math.ceil(series.length / MAX_BARS);
  const buckets = [];
  for (let i = 0; i < series.length; i += bucketSize) {
    const slice = series.slice(i, i + bucketSize);
    buckets.push({
      date: slice[0].date,
      endDate: slice[slice.length - 1].date,
      revenue: slice.reduce((sum, d) => sum + (d.repeat_revenue || 0), 0),
      orders: slice.reduce((sum, d) => sum + (d.repeat_orders || 0), 0),
    });
  }

  const maxRevenue = Math.max(...buckets.map(b => b.revenue), 1);
  const allZero = buckets.every(b => b.revenue === 0);
  const labelInterval = buckets.length <= 7 ? 1 : buckets.length <= 15 ? 2 : 5;
  const formatBucketLabel = (b) => {
    const d = new Date(b.date);
    // Multi-day buckets need the month for orientation; daily bars just the day.
    return bucketSize > 1 ? `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}` : `${d.getDate()}`;
  };

  return (
    <div>
      <div className="flex items-end h-40 gap-px w-full">
        {buckets.map((bucket, i) => (
          <div key={bucket.date} className="flex-1 flex flex-col items-center justify-end h-full min-w-0 relative group">
            <div
              className="w-full bg-green-500 rounded-t opacity-80 transition-all"
              style={{ height: `${Math.max((bucket.revenue / maxRevenue) * 130, bucket.revenue > 0 ? 8 : 2)}px` }}
            />
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                <p>${bucket.revenue.toLocaleString()}</p>
                <p className="text-gray-400">
                  {bucket.orders} order{bucket.orders !== 1 ? 's' : ''}
                  {bucketSize > 1 ? ` · ${bucket.date} – ${bucket.endDate}` : ` · ${bucket.date}`}
                </p>
              </div>
            </div>
            <span className={`text-xs mt-1 whitespace-nowrap ${i % labelInterval === 0 ? 'text-gray-500' : 'text-transparent'}`}>
              {formatBucketLabel(bucket)}
            </span>
          </div>
        ))}
      </div>
      {allZero && (
        <p className="text-sm text-gray-500 text-center mt-3">
          No attributed revenue yet — revenue shows here when a customer purchases
          within {windowDays} days of receiving an Ecomoria email.
        </p>
      )}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-12">
      <p className="text-gray-500">{message}</p>
    </div>
  );
}